<script setup lang="ts">
import { useRating } from '~/composables/useRating'
import type { ICompanyRating } from './types'
import { LucideChevronRight } from 'lucide-vue-next'

const { locale } = useI18n()
const config = useRuntimeConfig()
const { getRatingCriteriaSets } = useRating()

const CACHE_KEY = 'company-rating-banks'

// Fetch banks with ratings from MeiliSearch
// Nuxt's built-in data caching handles this efficiently
const { data: banksResult, pending: banksPending } = await useAsyncData(
  CACHE_KEY,
  async () => {
    try {
      const { search } = useMeiliSearch(`${config.public.meiliEnvironment}_banks`)
      const result = await search('', {
        filter: ['ratings IS NOT NULL'],
        limit: 1000
      })
      return result
    } catch (e) {
      console.error('Fetch error:', e)
      return { hits: [] }
    }
  },
  {
    server: true,
    default: () => ({ hits: [] })
  }
)

// Fetch criteria sets from API
const { data: criteriaSetsData, pending: criteriaPending } = getRatingCriteriaSets({
  rateable_type: 'bank',
  status: 'active'
})

// Map criteria id to name
const criteriaMap = computed(() => {
  const map = new Map<number, string>()
  const sets = criteriaSetsData.value?.data || []
  sets.forEach((set) => {
    set.criteria?.forEach((c) => {
      map.set(c.id, c.name)
    })
  })
  return map
})

// Transform banks data to ICompanyRating format
const companyRating = computed<ICompanyRating[]>(() => {
  const hits = (banksResult.value?.hits || []).filter((hit) => hit.ratings != null)
  return hits
    .map((hit) => {
      const criteriaAverages: Record<number, number> = {}

      // Extract criteria averages from ratings.criteria_set_averages.criteria
      if (hit.ratings?.criteria_set_averages?.criteria) {
        Object.entries(hit.ratings.criteria_set_averages.criteria).forEach(([id, data]) => {
          if (typeof data === 'object' && data !== null && 'average' in data) {
            criteriaAverages[Number(id)] = (data as { average: number }).average
          }
        })
      }

      return {
        id: hit.id,
        companyName: hit.title[locale.value] || hit.title.ru || hit.title.ky || '',
        companyLogo: hit.logo,
        url: hit.url,
        rating: Number(hit.ratings?.average_rate) || 0,
        weight: Number(hit.weight) || 0,
        criteriaAverages
      }
    })
    .sort((a, b) => b.rating - a.rating || b.weight - a.weight)
})

// Get sorted unique criteria ids for table columns
const displayCriteria = computed(() => {
  const allIds = new Set<number>()
  companyRating.value.forEach((entry) => {
    Object.keys(entry.criteriaAverages).forEach((id) => allIds.add(Number(id)))
  })
  return Array.from(allIds).sort()
})

// Get criteria name by id
const getCriteriaName = (id: number) => {
  return criteriaMap.value.get(id) || `Criteria ${id}`
}

// Get criteria average for a company
const getCriteriaAverage = (entry: ICompanyRating, criteriaId: number) => {
  return entry.criteriaAverages[criteriaId]?.toFixed(1) || '-'
}

const isLoading = computed(() => banksPending.value || criteriaPending.value)
</script>

<template>
  <div class="CompanyRating space-y-5 sm:space-y-6">
    <header class="CompanyRatingHeader">
      <Heading tag="h2">
        {{ $t('widget.home_page.company_rating.heading') }}
      </Heading>

      <Button variant="link" size="auto" class="mt-3 md:mt-0" as-child>
        <NuxtLinkLocale to="/banks">
          {{ $t('common.all_banks') }}
          <LucideChevronRight />
        </NuxtLinkLocale>
      </Button>
    </header>

    <!-- Loading skeleton -->
    <div v-if="isLoading" class="space-y-4">
      <div v-for="i in 5" :key="i" class="CompanyRatingLoadingItem" />
    </div>

    <!-- Empty state -->
    <div v-else-if="companyRating.length === 0" class="CompanyRatingEmpty">
      {{ $t('common.no_data') }}
    </div>

    <!-- Rating table -->
    <div v-else class="CompanyRatingTableWrapper">
      <Table>
        <TableHeader class="CompanyRatingTableHeader">
          <TableRow>
            <TableHead />
            <TableHead />
            <TableHead />
            <TableHead
              v-for="criteriaId in displayCriteria"
              :key="criteriaId"
              class="CompanyRatingCriteriaHead"
            >
              {{ getCriteriaName(criteriaId) }}
            </TableHead>
          </TableRow>
        </TableHeader>

        <TableBody class="CompanyRatingTableBody">
          <TableRow v-for="(entry, index) in companyRating" :key="entry.id" class="relative">
            <TableCell class="CompanyRatingIndex">
              {{ index + 1 }}
            </TableCell>

            <TableCell>
              <div class="CompanyRatingCompany">
                <NuxtLinkLocale :to="entry.url" class="CompanyRatingCompanyLogo">
                  <figure class="flex items-center justify-center">
                    <NuxtImg
                      :src="entry.companyLogo"
                      width="36"
                      height="36"
                      class="h-9 w-9"
                      format="avif,webp"
                      :alt="entry.companyName"
                    />
                  </figure>
                </NuxtLinkLocale>

                <NuxtLinkLocale :to="entry.url" class="CompanyRatingCompanyName">
                  {{ entry.companyName }}
                </NuxtLinkLocale>
              </div>
            </TableCell>

            <TableCell>
              <div class="CompanyRatingRating">
                <Rating :value="entry.rating" size="sm" />
                {{ entry.rating.toFixed(1) }}
              </div>
            </TableCell>

            <TableCell
              v-for="criteriaId in displayCriteria"
              :key="criteriaId"
              class="hidden lg:table-cell"
            >
              {{ getCriteriaAverage(entry, criteriaId) }}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  </div>
</template>

<style scoped>
.CompanyRatingHeader {
  @apply flex flex-wrap items-center justify-between gap-1.5 sm:gap-8;
}

.CompanyRatingLoadingItem {
  @apply h-16 animate-pulse rounded-lg bg-gray-200;
}

.CompanyRatingEmpty {
  @apply py-8 text-center text-gray-500;
}

.CompanyRatingTableWrapper {
  @apply overflow-hidden rounded-lg border;
}

.CompanyRatingTableHeader {
  @apply hidden lg:table-header-group;
}

.CompanyRatingTableHeader :deep(th) {
  @apply whitespace-nowrap text-center py-3.5;
}

.CompanyRatingCriteriaHead {
  @apply hidden !whitespace-normal break-words lg:table-cell;
}

.CompanyRatingTableBody :deep(td) {
  @apply text-center;
}

.CompanyRatingIndex {
  @apply hidden pr-0 lg:table-cell;
}

.CompanyRatingCompany {
  @apply flex items-center gap-2;
}

.CompanyRatingCompanyLogo {
  @apply flex h-9 w-9 items-center justify-center;
}

.CompanyRatingCompanyName {
  @apply whitespace-nowrap font-semibold hover:underline;
}

.CompanyRatingRating {
  @apply flex items-center justify-center gap-2 font-bold;
}
</style>
